/** @module Search the catalogue ("SVI 74", "74/198", names in any language) or the own collection. */
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useRouter, useSearch } from "@tanstack/react-router";
import { ChevronRight, Search, Sparkles, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { api, problemFrom } from "@/api/client";
import { CardFace } from "@/components/card-face";
import { PageHeader } from "@/components/page-header";
import { Segmented } from "@/components/ui/controls";
import { printedAs, printedNumber, variantLabel } from "@/lib/cards";
import { count } from "@/lib/format";

/** Typing pauses this long before a request goes out. */
const DEBOUNCE_MS = 300;

type Scope = "catalog" | "collection";

async function fetchResults(query: string) {
  const { data, response } = await api.GET("/api/v1/cards/search", {
    params: { query: { q: query, limit: 60 } },
  });
  if (!data) throw problemFrom(response);
  return data.items;
}

async function fetchOwned(query: string) {
  const { data, response } = await api.GET("/api/v1/collection/items", {
    params: { query: { q: query, limit: 200, with_total: true } },
  });
  if (!data) throw problemFrom(response);
  return data;
}

/** The typed text marked in a name, so it is clear why a card matched. */
export function Highlight({ text, query }: { text: string; query: string }) {
  const at = query ? text.toLocaleLowerCase().indexOf(query.toLocaleLowerCase()) : -1;
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <mark className="rounded-[3px] bg-(--accent-soft) px-[2px] text-(--accent-soft-ink)">
        {text.slice(at, at + query.length)}
      </mark>
      {text.slice(at + query.length)}
    </>
  );
}

export function SearchPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  const navigate = useNavigate();
  const router = useRouter();
  const { q = "" } = useSearch({ strict: false });
  const [text, setText] = useState(q);
  const [scope, setScope] = useState<Scope>("catalog");
  const query = q.trim();

  // The query lives in the address, so back and a shared link show the same results.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (text.trim() !== query)
        void navigate({ to: "/search", search: { q: text.trim() }, replace: true });
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [text, query, navigate]);

  const results = useQuery({
    queryKey: ["search", query],
    queryFn: () => fetchResults(query),
    enabled: query.length > 0,
    staleTime: 5 * 60_000,
  });
  const owned = useQuery({
    queryKey: ["search-owned", query],
    queryFn: () => fetchOwned(query),
    enabled: query.length > 0,
  });

  // How many copies of each hit are owned, for the "2 im Besitz" badge.
  const ownedCount = new Map<string, number>();
  for (const item of owned.data?.items ?? []) {
    ownedCount.set(item.print_id, (ownedCount.get(item.print_id) ?? 0) + 1);
  }
  const ownedTotal = owned.data?.total ?? owned.data?.items.length ?? 0;

  const field = (
    <label className="input-search grow md:max-w-xl">
      <Search />
      <input
        // Plain text with the keyboard's search key: type="search" brings a second clear cross.
        type="text"
        enterKeyHint="search"
        // A page whose whole point is the field may take the focus.
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        value={text}
        onChange={(event) => {
          setText(event.target.value);
        }}
        placeholder={t("catalog.searchHint")}
        aria-label={t("nav.search")}
        className="input pr-10"
      />
      {text ? (
        <button
          type="button"
          className="absolute right-2 grid size-8 place-items-center"
          aria-label={t("search.clear")}
          onClick={() => {
            setText("");
          }}
        >
          <X className="size-4" />
        </button>
      ) : null}
    </label>
  );

  let body;
  if (!query) {
    body = null;
  } else if (scope === "catalog") {
    const items = results.data ?? [];
    body = results.isPending ? (
      <p className="t-muted">{t("common.loading")}</p>
    ) : results.isError ? (
      <p role="alert" className="loss">
        {t("common.loadError")}
      </p>
    ) : items.length === 0 ? (
      <p className="t-muted">{t("catalog.noResults")}</p>
    ) : (
      <section className="flex flex-col gap-[2px]">
        <span className="t-label">{t("search.hits", { count: count(items.length, locale) })}</span>
        <ul>
          {items.map((card) => {
            const mine = ownedCount.get(card.id) ?? 0;
            return (
              <li key={card.id}>
                <Link
                  to="/cards/$printId"
                  params={{ printId: card.id }}
                  className="flex min-h-14 items-center gap-[10px] border-b border-(--border) py-2 text-(--text) no-underline"
                >
                  <span className="thumb">
                    <CardFace printId={card.id} name={card.name} lazy />
                  </span>
                  <span className="flex min-w-0 grow flex-col">
                    <b className="t-sm truncate">
                      <Highlight text={card.name} query={query} />
                    </b>
                    <span className="t-xs t-muted truncate">
                      {card.set_name ? `${card.set_name} · ` : ""}
                      <span className="num">{printedAs(card, card.id)}</span>
                      {card.rarity ? ` · ${card.rarity}` : ""}
                    </span>
                  </span>
                  {mine > 0 ? (
                    <span className="badge badge-neutral">
                      {t("search.owned", { count: mine })}
                    </span>
                  ) : (
                    <ChevronRight className="size-5 shrink-0 text-(--text-muted)" />
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </section>
    );
  } else {
    const items = owned.data?.items ?? [];
    body = owned.isPending ? (
      <p className="t-muted">{t("common.loading")}</p>
    ) : items.length === 0 ? (
      <p className="t-muted">{t("search.noneOwned")}</p>
    ) : (
      <section className="flex flex-col gap-[2px]">
        <span className="t-label">{t("search.hits", { count: count(ownedTotal, locale) })}</span>
        <ul>
          {items.map((item) => {
            const name = item.card?.name ?? item.print_id;
            return (
              <li key={item.id}>
                <Link
                  to="/cards/$printId"
                  params={{ printId: item.print_id }}
                  className="flex min-h-14 items-center gap-[10px] border-b border-(--border) py-2 text-(--text) no-underline"
                >
                  <span className="thumb">
                    <CardFace printId={item.print_id} name={name} lazy />
                  </span>
                  <span className="flex min-w-0 grow flex-col">
                    <b className="t-sm truncate">
                      <Highlight text={name} query={query} />
                    </b>
                    <span className="t-xs t-muted num truncate">
                      {item.card ? printedNumber(item.card) : item.print_id}
                      {item.card
                        ? ` · ${variantLabel(item.card.variant_key, (key, fallback) => t(key, fallback))}`
                        : ""}
                      {` · ${item.condition}`}
                    </span>
                  </span>
                  <span className="lang">{item.language.toUpperCase()}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </section>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <span className="hidden md:block">
        <PageHeader title={t("nav.search")} global={false} />
      </span>
      {/* Phones: the field is the app bar, with a way back next to it. */}
      <div className="row gap-2 md:hidden">
        {field}
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => {
            router.history.back();
          }}
        >
          {t("common.cancel")}
        </button>
      </div>
      <span className="hidden md:block">{field}</span>
      {query ? (
        <Segmented
          className="self-start"
          label={t("search.scope")}
          value={scope}
          onChange={setScope}
          options={[
            { value: "catalog", label: t("search.catalog") },
            {
              value: "collection",
              label: owned.data
                ? t("search.collectionCount", { count: count(ownedTotal, locale) })
                : t("nav.collection"),
            },
          ]}
        />
      ) : null}
      {body}
      <div className="callout callout-info md:max-w-xl">
        <Sparkles />
        <span>{t("search.tip")}</span>
      </div>
    </div>
  );
}
